import { HttpClient } from './http-client';
import { APIResponse, CLVisionConfig, PageViewEvent, RequestConfig, SignupEvent, SigninEvent } from './types';
import { CookieManager } from './utils/cookie';

export class CLVision {
  private static instance: CLVision | null = null;
  private static isInitialized = false;
  private readonly config: RequestConfig;
  private cookieId: string;
  private readonly http: HttpClient;
  private static DEFAULT_BASE_URL = 'https://clvision.app/api/tracking'; // Updated DEFAULT_BASE_URL
  private currentPageUrl: string | null = null;

  constructor(options: CLVisionConfig) {
    // Validate options but defer initialization
    if (!options.organizationId) throw new Error('Organization ID is required');

    this.cookieId = CookieManager.getCookieId();
    this.config = {
      headers: {
        'X-Organization-Id': options.organizationId,
        'X-Cookie-Id': this.cookieId,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      baseURL: options.baseURL || CLVision.DEFAULT_BASE_URL // Updated baseURL
    };
    this.http = new HttpClient(this.config);

    // Immediate initialization for client-side
    if (typeof window !== 'undefined') {
      this.initializeClient();
    }
  }

  private initializeClient(): void {
    if (CLVision.isInitialized) return;
    
    this.doInitialize();

    // Backup initialization for hydration
    if (document.readyState !== 'complete') {
      window.addEventListener('load', () => this.doInitialize());
    }

    // Handle Next.js client-side navigation
    if ('navigation' in window) {
      (window as any).navigation.addEventListener('navigate', () => {
        this.trackPageView();
      });
    }

    // Listen for route changes in Next.js
    if (typeof window !== 'undefined') {
      const pushState = history.pushState;
      history.pushState = (...args) => {
        pushState.apply(history, args);
        this.trackPageView();
      };
      
      window.addEventListener('popstate', () => this.trackPageView());
    }
  }

  private doInitialize(): void {
    if (CLVision.isInitialized) return;
    
    this.cookieId = this.initializeCookieId();
    this.config.headers['X-Cookie-Id'] = this.cookieId;
    
    // Force initial page view
    this.trackPageView();
    
    CLVision.isInitialized = true;
  }

  static getInstance(config: CLVisionConfig): CLVision {
    if (!CLVision.instance) {
      CLVision.instance = new CLVision(config);
    }
    return CLVision.instance;
  }

  private initializeCookieId(): string {
    if (typeof window === 'undefined') return '';
    
    let cookieId = CookieManager.getCookieId();
    if (!cookieId) {
      cookieId = CookieManager.generateUUID();
      CookieManager.setCookie(cookieId,[]);
    }
    return cookieId;
  }

  private initializePageViewTracking(): void {
    if (typeof window === 'undefined') return;
    this.trackPageView();
    window.addEventListener('popstate', () => this.trackPageView());
  }

  private async trackPageView(): Promise<void> {
    const currentUrl = window.location.href;
    if (currentUrl === this.currentPageUrl) return;
    
    this.currentPageUrl = currentUrl;
    const pageViewEvent: PageViewEvent = {
      url: currentUrl,
      referrer: document.referrer || '',
      timestamp: Date.now(),
      cookieId: this.cookieId
    };

    try {
      await this.http.post<APIResponse>('/events/pageview', pageViewEvent);
    } catch (error) {
      console.error('Failed to send page view event:', error);
    }
  }

  /**
   * Track a user sign-up event
   */
  public async trackSignup(email: string): Promise<void> {
    const event: SignupEvent = {
      email,
      cookieId: this.cookieId,
      timestamp: Date.now(),
      url: window.location.href,
      referrer: document.referrer || '',
    };
    try {
      await this.http.post<APIResponse>(`/events/signup`, event);
      CookieManager.addEmail(email);
    } catch (error) {
      console.error(`Failed to send signup event:`, error);
    }
  }

  /**
   * Track a user sign-in event
   */
  public async trackSignin(email: string): Promise<void> {
    const event: SigninEvent = {
      email,
      cookieId: this.cookieId,
      timestamp: Date.now(),
      url: window.location.href,
      referrer: document.referrer || '',
    };
    try {
      await this.http.post<APIResponse>(`/events/signin`, event);
      CookieManager.addEmail(email);
    } catch (error) {
      console.error(`Failed to send signup event:`, error);
    }
  }

  // Method to get config (useful for testing)
  getConfig(): RequestConfig {
    return this.config;
  }
}

// Create and export a singleton instance
let globalInstance: CLVision | null = null;

export function initialize(config: CLVisionConfig): CLVision {
  if (!globalInstance) {
    globalInstance = new CLVision(config);
  }
  return globalInstance;
}

// Browser compatibility
if (typeof window !== 'undefined') {
  (window as any).CLVision = CLVision;
}

export * from './types';
